import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { IDog } from "@eagerdog/interfaces";
import { DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";

import { Config } from "@eagerdog/config";
import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import { Modal, useModal } from "src/components/Modal/Modal";
import DogForm from "src/components/StepForm/DogForm/DogForm";
import Confirm from "src/components/Confirm/Confirm";
import ShareModal from "src/components/ShareModal/ShareModal";
import Icon from "src/components/Icon/Icon";

import pencilIcon from "src/assets/icons/pencil.svg";
import deleteIcon from "src/assets/icons/delete.svg";
import shareIcon from "src/assets/icons/share.svg";

import styles from "./MyDogs.module.scss";

// Total distinct base titles tracked across all sports in EagerDog:
//   Fast CAT (3) + Nosework (42) + Scent Work (25) + Fetch (4) +
//   Obedience (4) + Rally Obedience (7) + UKC Obedience (5) +
//   UKC Rally (10) + Barn Hunt (16) = 116
const TOTAL_TRACKABLE_TITLES = 116;

function countEarnedTitles(ribbonData: any): number {
    const sports: any[] = ribbonData.sports ?? [];
    return sports.reduce((sum, sport) => sum + (sport.earned_titles?.length ?? 0), 0);
}

function sortByNewest(dogs: IDog[]): IDog[] {
    return [...dogs].sort((a, b) => {
        const tsA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tsB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tsB - tsA;
    });
}

const AnimatedProgressBar: React.FC<{ earned: number }> = ({ earned }) => {
    const pct = Math.min((earned / TOTAL_TRACKABLE_TITLES) * 100, 100);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const outer = requestAnimationFrame(() => {
            const inner = requestAnimationFrame(() => setWidth(pct));
            return () => cancelAnimationFrame(inner);
        });
        return () => cancelAnimationFrame(outer);
    }, [pct]);

    return (
        <div className={styles.progressWrap}>
            <div className={styles.progressBar}>
                <div className={styles.progressInner} style={{ width: `${width}%` }} />
            </div>
            <span className={styles.progressLabel}>{earned} / {TOTAL_TRACKABLE_TITLES} Titles</span>
        </div>
    );
};

const MyDogs: React.FC = () => {
    const user = helperService.getUser();
    // Dog Owner Premium is retired: Ribbon Room data is free for everyone.
    const isPremium = !DOG_OWNER_PREMIUM_ENABLED || !!user.tariff_plan_subscription_id;

    const [dogs, setDogs] = useState<IDog[]>([]);
    const [titleCountMap, setTitleCountMap] = useState<Record<string, number | null>>({});
    const [titlesFetched, setTitlesFetched] = useState<boolean>(false);
    const [selectedDog, setSelectedDog] = useState<IDog | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);
    const editModal = useModal();
    const deleteModal = useModal();
    const shareModal = useModal();

    useEffect(() => {
        if (!user._id) return;
        apiService.getUserDogs(user._id).then((response: IDog[]) => {
            setDogs(sortByNewest(response || []));
        }).catch(() => {});
    }, [user._id]);

    useEffect(() => {
        if (!isPremium || dogs.length === 0) return;

        const visibleDogs = dogs.slice(0, 3);

        Promise.all(
            visibleDogs.map(async (dog) => {
                try {
                    const data = await apiService.getRibbonRoomData(dog._id);
                    return { dogId: dog._id, count: countEarnedTitles(data) };
                } catch {
                    return { dogId: dog._id, count: null };
                }
            })
        ).then((results) => {
            const map: Record<string, number | null> = {};
            for (const { dogId, count } of results) {
                map[dogId] = count;
            }
            setTitleCountMap(map);
            setTitlesFetched(true);
        });
    }, [dogs, isPremium]);

    const handleDelete = () => {
        if (!selectedDog) return;
        setDeleting(true);
        apiService.updateDog(selectedDog._id, { ...selectedDog, is_archived: true })
            .then(() => {
                setDogs((prev) => prev.filter((d) => d._id !== selectedDog._id));
                deleteModal.toggle();
            })
            .catch(() => {})
            .finally(() => setDeleting(false));
    };

    if (dogs.length === 0) return null;

    return (
        <div className={styles.MyDogs}>
            <div className={styles.titleRow}>
                <span className={styles.title}>My Dogs</span>
                <button
                    className={styles.addBtn}
                    title="Add Dog"
                    onClick={() => { setSelectedDog(null); editModal.toggle(); }}
                >
                    <Icon type="plus" className={styles.addBtnIcon} />
                </button>
            </div>
            <ul className={styles.list}>
                {dogs.slice(0, 3).map((dog) => {
                    const earned = isPremium && titlesFetched ? titleCountMap[dog._id] : undefined;
                    const hasData = earned != null && earned > 0;

                    return (
                        <li key={dog._id} className={styles.item}>
                            <Link to={`/dogs/${dog._id}`} className={styles.inner}>
                                <div className={styles.portrait}>
                                    {dog.image_url ? (
                                        <img
                                            src={dog.image_url}
                                            alt={dog.call_name}
                                            className={styles.portraitImage}
                                        />
                                    ) : (
                                        <div className={styles.portraitFallback}>
                                            {(dog.call_name || dog.registered_name || "?").charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.content}>
                                    <div className={styles.nameRow}>
                                        <span className={styles.name}>
                                            {dog.call_name || dog.registered_name}
                                        </span>
                                    </div>
                                    {(dog.breed || dog.variety) && (
                                        <span className={styles.registeredName}>
                                            {[dog.breed, dog.variety].filter(Boolean).join(" – ")}
                                        </span>
                                    )}
                                    {isPremium && titlesFetched && (
                                        hasData ? (
                                            <AnimatedProgressBar earned={earned!} />
                                        ) : (
                                            <span className={styles.noTrackingData}>
                                                No titles earned yet
                                            </span>
                                        )
                                    )}
                                </div>
                            </Link>
                            <div className={styles.actions}>
                                <button
                                    className={styles.editBtn}
                                    title="Edit Dog"
                                    onClick={() => { setSelectedDog(dog); editModal.toggle(); }}
                                >
                                    <img src={pencilIcon} alt="Edit" />
                                </button>
                                <button
                                    className={styles.deleteBtn}
                                    title="Delete Dog"
                                    onClick={() => { setSelectedDog(dog); deleteModal.toggle(); }}
                                >
                                    <img src={deleteIcon} alt="Delete" />
                                </button>
                            </div>
                            <button
                                className={styles.shareBtn}
                                onClick={() => { setSelectedDog(dog); shareModal.toggle(); }}
                            >
                                Share
                            </button>
                        </li>
                    );
                })}
            </ul>
            <div className={styles.viewAll}>
                <Link to="/account?t=0">View All Dogs →</Link>
            </div>

            <Modal
                className="dogForm"
                isShown={editModal.isShown}
                hide={editModal.toggle}
                modalContent={
                    <DogForm
                        dog={selectedDog ?? undefined}
                        onDogUpdated={(updatedDog) => setSelectedDog(updatedDog)}
                        onFinish={(updatedDog) => {
                            if (updatedDog) {
                                setDogs((prev) => {
                                    const exists = prev.some((d) => d._id === updatedDog._id);
                                    const next = exists
                                        ? prev.map((d) => (d._id === updatedDog._id ? updatedDog : d))
                                        : [...prev, updatedDog];
                                    return sortByNewest(next);
                                });
                            }
                            editModal.toggle();
                        }}
                    />
                }
            />

            <Modal
                className="Confirmation"
                isShown={deleteModal.isShown}
                hide={deleteModal.toggle}
                modalContent={
                    <Confirm
                        message={<>Are you sure you want to delete <strong>{selectedDog?.call_name || selectedDog?.registered_name}</strong>?</>}
                        submitting={deleting}
                        buttonsDisabled={deleting}
                        onConfirm={handleDelete}
                        onCancel={deleteModal.toggle}
                    />
                }
            />

            <Modal
                className="ShareModalContainer"
                isShown={shareModal.isShown}
                hide={shareModal.toggle}
                modalContent={
                    <ShareModal
                        eventTitle={selectedDog?.call_name || selectedDog?.registered_name || ""}
                        eventUrl={`${Config.web.REACT_APP_API_URL || window.location.origin}/preview/dog/${selectedDog?._id}`}
                        shareType="dog"
                        onClose={shareModal.toggle}
                    />
                }
            />
        </div>
    );
};

export default MyDogs;
